import React, { forwardRef } from 'react';

export type EditorButtonProps = {
  /** Toggled-on state, e.g. the cursor sits inside bold text. */
  active?: boolean;
  /** Only meaningful for the history variant, which dims instead of hiding. */
  disabled?: boolean;
  variant?: 'default' | 'history';
  /**
   * Deliberately `onMouseDown`, not `onClick`.
   *
   * Toolbar controls must act on the editor's current selection. A click fires
   * after focus has already moved, so these handlers run on mousedown and call
   * `preventDefault()` to stop focus leaving the editor at all.
   */
  onMouseDown: (event: React.MouseEvent<HTMLSpanElement>) => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * A Slate toolbar control.
 *
 * Intentionally a `<span>` rather than the shared `Button` primitive: a real
 * `<button>` takes focus on mousedown and destroys the editor selection the
 * toolbar is about to act on. That different contract is why this is a separate
 * component instead of a variant.
 *
 * It absorbs the `editor-button${isActive ? ' editor-button--active' : ''}`
 * class-concatenation line, which was written out identically six times, and
 * adds the button semantics the bare spans never had.
 */
/** Forwards its ref: ColorPickerButton measures this element to place a dropdown. */
const EditorButton = forwardRef<HTMLSpanElement, EditorButtonProps>(function EditorButton(
  {
    active = false,
    disabled = false,
    variant = 'default',
    onMouseDown,
    title,
    className,
    children,
  },
  ref
) {
  const classNames = [
    'editor-button',
    variant === 'history' ? 'editor-button--history' : undefined,
    active ? 'editor-button--active' : undefined,
    !active && disabled ? 'editor-button--disabled' : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      ref={ref}
      className={classNames}
      onMouseDown={onMouseDown}
      role='button'
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      title={title}
      // Kept out of the tab order on purpose: focus belongs to the editor.
      tabIndex={-1}
    >
      {children}
    </span>
  );
});

EditorButton.displayName = 'EditorButton';
export default EditorButton;
